import { Link } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Route as RouteIcon, Target, TrendingUp } from "lucide-react";
import SEO from "@/components/SEO";
import { useLanguage } from "@/i18n/LanguageContext";
import { businessCases } from "@/data/businessCases";
import { SITE_URL } from "@/lib/site-url";

/**
 * Ficha indexable de un caso redactado (`src/data/businessCases.ts`).
 * URL propia: /casos-reales/{slug} y /en/real-cases/{slug}.
 */
const BusinessCasePage = ({ slug, routeLang }: { slug: string; routeLang: "es" | "en" }) => {
  const { lang: ctxLang } = useLanguage();
  const lang = routeLang ?? ctxLang;
  const es = lang === "es";
  const caseData = businessCases.find((c) => c.slug === slug);
  const listPath = es ? "/casos-reales" : "/en/real-cases";

  if (!caseData) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-16">
        <h1 className="font-display text-2xl font-bold text-foreground">
          {es ? "Caso no encontrado" : "Case not found"}
        </h1>
        <Link to={listPath} className="mt-4 inline-block font-body text-sm underline">
          {es ? "Ver todos los casos" : "See every case"}
        </Link>
      </main>
    );
  }

  const title = es ? caseData.title : caseData.titleEn;
  const profile = es ? caseData.profileLabel : caseData.profileLabelEn;
  const challenge = es ? caseData.challenge : caseData.challengeEn;
  const strategy = es ? caseData.strategy : caseData.strategyEn;
  const results = es ? caseData.results : caseData.resultsEn;
  const pathEs = `/casos-reales/${caseData.slug}`;
  const pathEn = `/en/real-cases/${caseData.slug}`;
  const others = businessCases.filter((c) => c.slug !== caseData.slug).slice(0, 3);

  return (
    <main className="mx-auto w-full max-w-3xl space-y-8 px-4 py-10 md:py-14">
      <SEO
        title={`${title} | Retail Lemon`}
        description={challenge.slice(0, 155)}
        path={es ? pathEs : pathEn}
        pathEs={pathEs}
        pathEn={pathEn}
        lang={lang}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description: challenge.slice(0, 200),
          inLanguage: es ? "es-ES" : "en",
          mainEntityOfPage: `${SITE_URL}${es ? pathEs : pathEn}`,
          publisher: { "@type": "Organization", name: "Retail Lemon", url: SITE_URL },
        }}
      />

      <Link
        to={listPath}
        className="inline-flex items-center gap-1.5 font-body text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {es ? "Todos los casos" : "All cases"}
      </Link>

      <header>
        <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">{profile}</span>
        <h1 className="mt-2 font-display text-2xl font-bold leading-snug text-foreground md:text-3xl">{title}</h1>
      </header>

      <div className="flex flex-wrap gap-3">
        {caseData.metrics.map((m, i) => (
          <div key={i} className="min-w-[100px] rounded-xl bg-foreground px-5 py-3 text-center">
            <span className="block font-display text-2xl font-bold text-foreground md:text-3xl">{m.value}</span>
            <span className="mt-0.5 block font-body text-[11px] uppercase tracking-wide text-background/70">
              {es ? m.label : m.labelEn}
            </span>
          </div>
        ))}
      </div>

      <section>
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-destructive/10">
            <Target className="h-4 w-4 text-destructive" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-xl font-bold text-foreground">{es ? "El reto" : "The challenge"}</h2>
        </div>
        <p className="font-body leading-relaxed text-muted-foreground">{challenge}</p>
      </section>

      <section>
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15">
            <RouteIcon className="h-4 w-4 text-foreground" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-xl font-bold text-foreground">
            {es ? "La estrategia" : "The strategy"}
          </h2>
        </div>
        <ol className="list-none space-y-3 pl-0">
          {strategy.map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary font-body text-xs font-bold text-foreground">
                {i + 1}
              </span>
              <p className="pt-0.5 font-body leading-relaxed text-muted-foreground">{step}</p>
            </li>
          ))}
        </ol>
      </section>

      <section>
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15">
            <TrendingUp className="h-4 w-4 text-foreground" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-xl font-bold text-foreground">
            {es ? "Los resultados" : "The results"}
          </h2>
        </div>
        <ul className="list-none space-y-2.5 pl-0">
          {results.map((r, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-foreground" strokeWidth={1.5} />
              <p className="font-body font-medium leading-relaxed text-foreground">{r}</p>
            </li>
          ))}
        </ul>
      </section>

      {others.length > 0 && (
        <section className="border-t border-border pt-6">
          <h2 className="font-display text-lg font-bold text-foreground">
            {es ? "Otros casos" : "Other cases"}
          </h2>
          <ul className="mt-3 space-y-2">
            {others.map((c) => (
              <li key={c.slug}>
                <Link
                  to={es ? "/casos-reales/$slug" : "/en/real-cases/$slug"}
                  params={{ slug: c.slug }}
                  className="font-body text-sm text-muted-foreground underline transition-colors hover:text-foreground"
                >
                  {es ? c.title : c.titleEn}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
};

export default BusinessCasePage;
